import { CheckCircle2, CircleSlash, Database, GitPullRequest, Save, Sparkles, Webhook } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { SEV_DOT } from '../components/ui/Badges';
import { Button } from '../components/ui/Button';
import { ErrorState, Notice, PageHeader, Skeleton } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';
import { useConfig } from '../hooks/queries';
import { useMe } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import { buttonClasses } from '../components/ui/Button';
import { usePreferences } from '../hooks/usePreferences';
import { cn } from '../lib/cn';
import { SEVERITY_LABEL } from '../lib/format';
import type { ReviewDepth, Severity } from '../types/api';
import { SEVERITIES } from '../types/api';

function StatusRow({ ok, label, detail }: { ok: boolean; label: string; detail?: ReactNode }) {
  return (
    <li className="flex items-start gap-3 py-2.5">
      {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-label="OK" /> : <CircleSlash className="mt-0.5 h-4 w-4 shrink-0 text-faint" aria-label="Not configured" />}
      <div className="min-w-0">
        <p className="text-sm">{label}</p>
        {detail && <p className="text-xs text-muted">{detail}</p>}
      </div>
    </li>
  );
}

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="card p-5">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <span className="text-muted">{icon}</span>
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function SettingsPage() {
  const { data: config, isLoading, error, refetch } = useConfig();
  const { data: me } = useMe();
  const { prefs, save } = usePreferences();
  const [depth, setDepth] = useState<ReviewDepth>(prefs.defaultDepth);
  const [severities, setSeverities] = useState<Severity[]>(prefs.defaultSeverities);
  const toast = useToast();

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (error || !config) return <ErrorState title="Could not load settings" error={error} onRetry={() => void refetch()} />;

  const dirty = depth !== prefs.defaultDepth || severities.join() !== prefs.defaultSeverities.join();

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Settings"
        color="orange"
        description="Integration status is read from the server. Secrets live only in server environment variables and are never shown in full."
      />

      {me && (
        <section className="card mb-5 flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between" aria-labelledby="account-heading">
          <div>
            <h2 id="account-heading" className="text-base font-semibold">
              {me.name}
            </h2>
            <p className="text-sm text-muted">{me.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted">
              <span className="text-lg font-semibold text-chalk-yellow">{me.credits}</span> review credit{me.credits === 1 ? '' : 's'}
            </span>
            <Link to="/app/credits" className={buttonClasses('secondary', 'sm')}>
              Manage credits
            </Link>
          </div>
        </section>
      )}

      <div className="grid gap-5 md:grid-cols-2">
        <Section icon={<Sparkles className="h-4 w-4" />} title="AI Pilot">
          <ul className="divide-y divide-line">
            <StatusRow ok={config.claude.apiKeyPresent} label="API key" detail={config.claude.apiKeyHint ?? 'Not set on the server'} />
            <StatusRow ok={!!config.claude.model} label="Model" detail={config.claude.model ? 'Configured on the server' : 'Not set on the server'} />
            <StatusRow
              ok={config.claude.configured}
              label={config.claude.configured ? 'Real reviews enabled' : 'Demo mode only'}
              detail={`Timeout ${config.claude.timeoutMs / 1000}s · max ${config.claude.maxRetries} retries · output ${config.claude.maxOutputTokens.quick}/${config.claude.maxOutputTokens.thorough} tokens (quick/thorough)`}
            />
          </ul>
        </Section>

        <Section icon={<GitPullRequest className="h-4 w-4" />} title="GitHub">
          <ul className="divide-y divide-line">
            <StatusRow ok={config.github.mode === 'live'} label={config.github.mode === 'live' ? 'Live GitHub API' : 'Demo mode (sample PRs only)'} />
            <StatusRow
              ok={config.github.tokenConfigured}
              label="Access token"
              detail={config.github.tokenHint ?? 'Not set — public repositories only, 60 requests/hour'}
            />
            <StatusRow ok={config.github.canPublish} label="Comment publishing" detail={config.github.canPublish ? 'Available after explicit confirmation' : 'Requires GITHUB_TOKEN'} />
          </ul>
        </Section>

        <Section icon={<Database className="h-4 w-4" />} title="Storage & data retention">
          <ul className="divide-y divide-line">
            <StatusRow ok={config.storage.persistent} label={config.storage.persistent ? 'MongoDB' : 'In-memory (not persisted)'} />
            <StatusRow
              ok={config.storage.retentionDays > 0}
              label="Retention"
              detail={
                config.storage.retentionDays > 0
                  ? `Reviews and findings are deleted after ${config.storage.retentionDays} days (DATA_RETENTION_DAYS).`
                  : 'Kept until deleted (DATA_RETENTION_DAYS=0).'
              }
            />
          </ul>
          <p className="mt-2 text-xs text-faint">
            Limits: {config.limits.maxFiles} files, {Math.round(config.limits.maxPatchBytesPerFile / 1000)} KB per file,{' '}
            {Math.round(config.limits.maxTotalDiffBytes / 1000)} KB total diff per review.
          </p>
        </Section>

        <Section icon={<Webhook className="h-4 w-4" />} title="Webhook automation">
          <ul className="divide-y divide-line">
            <StatusRow
              ok={config.webhook.enabled}
              label={config.webhook.enabled ? 'Enabled' : 'Disabled'}
              detail={
                config.webhook.enabled
                  ? `${config.webhook.allowlistCount} repository(ies) allowlisted. Endpoint: POST /api/webhooks/github`
                  : 'Set WEBHOOK_ENABLED=true and GITHUB_WEBHOOK_SECRET to review PRs automatically.'
              }
            />
          </ul>
        </Section>
      </div>

      <section className="card mt-5 p-5" aria-labelledby="prefs-heading">
        <h2 id="prefs-heading" className="text-sm font-semibold">
          Review preferences
        </h2>
        <p className="mt-1 text-xs text-muted">Saved in this browser.</p>

        <fieldset className="mt-4">
          <legend className="text-sm font-medium">Default review depth</legend>
          <div className="mt-2 flex gap-2">
            {(['quick', 'thorough'] as const).map((d) => (
              <label
                key={d}
                className={cn('cursor-pointer rounded-md border px-3 py-1.5 text-sm capitalize', depth === d ? 'border-accent bg-accent/5' : 'border-line')}
              >
                <input type="radio" name="default-depth" className="sr-only" checked={depth === d} onChange={() => setDepth(d)} />
                {d}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="mt-5">
          <legend className="text-sm font-medium">Default severity filter for findings</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {SEVERITIES.map((s) => {
              const on = severities.includes(s);
              return (
                <label key={s} className={cn('flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm', on ? 'border-line bg-raised' : 'border-dashed border-line text-faint')}>
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-[rgb(var(--accent))]"
                    checked={on}
                    onChange={() => setSeverities(on ? severities.filter((x) => x !== s) : [...severities, s])}
                  />
                  <span className={cn('h-2 w-2 rounded-full', SEV_DOT[s])} aria-hidden />
                  {SEVERITY_LABEL[s]}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="mt-5 flex justify-end">
          <Button
            variant="primary"
            disabled={!dirty}
            icon={<Save className="h-4 w-4" />}
            onClick={() => {
              save({ defaultDepth: depth, defaultSeverities: SEVERITIES.filter((s) => severities.includes(s)) });
              toast('Preferences saved.', 'success');
            }}
          >
            Save preferences
          </Button>
        </div>
      </section>

      <Notice className="mt-5" title="Where are API keys configured?">
        AI and GitHub credentials are read from server environment variables (see <span className="font-mono">.env.example</span>). They are
        never sent to the browser, stored in the database, or written to logs.
      </Notice>
    </div>
  );
}
